'use client'

import { useState, FormEvent } from 'react'
import Link from 'next/link'
import { sendPasswordResetEmail } from '@/app/actions/auth'

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
    const [message, setMessage] = useState('')

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setStatus('loading')
        setMessage('')

        try {
            const result = await sendPasswordResetEmail(email)

            if (result.success) {
                setStatus('success')
                setMessage('Si el correo pertenece a una cuenta, te enviaremos un enlace para restablecer tu contraseña.')
            } else {
                setStatus('error')
                setMessage(result.error || 'Hubo un error al procesar tu solicitud.')
            }
        } catch (err) {
            setStatus('error')
            setMessage('Hubo un error inesperado al procesar tu solicitud.')
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-lg shadow-xl p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Recuperar Contraseña</h1>
                        <p className="text-gray-600">Te enviaremos un enlace para que crees una nueva.</p>
                    </div>

                    {status === 'success' ? (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                            <p className="text-green-700 font-medium mb-4">{message}</p>
                            <Link href="/login" className="w-full inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">
                                Volver al inicio de sesión
                            </Link>
                        </div>
                    ) : (
                        <>
                            {status === 'error' && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-600 text-sm">{message}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                                        placeholder="tu@email.com"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {status === 'loading' ? 'Enviando...' : 'Enviar enlace de recuperación'}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="mt-8 text-center border-t border-gray-200 pt-6">
                        <p className="text-gray-600">
                            ¿Recordaste tu contraseña?{' '}
                            <Link href="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                                Inicia sesión aquí
                            </Link>
                        </p>
                    </div>

                    <div className="mt-4 text-center">
                        <Link href="/" className="text-gray-500 hover:text-gray-700 text-sm">
                            ← Volver al inicio
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}
